import Vue from 'vue';
import { Component , Mixins} from 'vue-property-decorator';
import KEYS from '@/values/keys';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength } from 'vuelidate/lib/validators'
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin';
import { SECTION_TYPES } from '@/content/values/sections';
import { IParamsCreateObject } from '@/courses/interfaces/params-create-object.interface';
import { Section } from '@/courses/models/section.model';
import ToolService from '@/providers/tools';

@Component({
  name: 'ed-course-create-multimedia-object-component',
  components: {
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdCourseCreateMultimediaObject extends Mixins(CreateObjectsMixin) {

  /* Data */
  public form: any = {};
  public dropzoneMode = KEYS.DROPZONE_CONFIG.SINGLE;
  public dropzoneOptions: any = {
    url: 'http://eduac.com',
    acceptedFiles: '.mp3,.wav,.mp4',
    autoProcessQueue: false,
    addRemoveLinks: true,
    uploadMultiple: false,
    maxFiles: 1,
    parallelUploads: 1,
    createImageThumbnails: false,
    maxFilesize: 200,
    timeout: 600000
  };
  private type: string = SECTION_TYPES.LESSON_TEMP_FILE.code;
  private file: any = null;
  private newFileAdded: boolean = false;
  public ext: string = '';
  public saving: boolean = false;
  public tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  public tagSelected: string = '';

  /* Computed */
  get title(): string {
    if(this.edition) return String(this.$i18n.t('courses.objects.editMultimedia'));
    return String(this.$i18n.t('courses.objects.createMultimedia'));
  }

  get section(): Section {
    return this.params.section;
  }

  get edition(): boolean {
    return this.params.edition;
  }

  get escapedName(): string {
    return ToolService.exitSpecialChars(this.form.name.value);
  }

  get hasFile(): boolean{
    return this.file !== null;
  }

  /* Methods */
  private initForm(name: string = '') {
    this.form = EdFormModel([
      EdInputModel('name', name , { required , maxLength : maxLength(60)})
    ]);
  }

  private async saveNewFile(data: IParamsCreateObject) {
    try {
      await this.createObject(data);
      this.closeModal();
    } catch {
      this.setSaving(false);
    }
  }

  private async updateFile(section: Section) {
    let refreshData = {
      sectionId: this.section.sectionId,
      parentId: this.params.parentId, 
      courseId: this.params.courseId,
      classId: this.params.classId
    };
    try {
      if(this.newFileAdded) {
        await this.$Courses.Store.removeFileA(this.section);
        await this.$Courses.Store.updateFileA({
          file: this.file,
          sectionId: this.section.sectionId,
          courseId: this.params.courseId,
        });
      }      
      await this.editObject(section);
      await this.goToRefreshSectionsTree(refreshData);
      this.closeModal();
    } catch {
      this.setSaving(false);
      this.showModalError();
    }
  }

  private setDataToSave(): IParamsCreateObject {
    return {
      type: this.type,
      name: this.form.name.value,
      parentId: this.params.parentId,
      courseId: this.params.courseId,
      file: this.file,
      labelSections: this.tagSelected,
      component: this.componentSelected
    }
  }

  private getExtension(filename: string) {
    let fileArr = filename.split('.');
    return fileArr[fileArr.length - 1] ? fileArr[fileArr.length - 1] : '';
  }

  private setFileName(filename: string) {
    let nameParts: string[] = filename.split('.');
    let name: string = filename.replace(
      `.${nameParts[nameParts.length - 1]}`,
      ''
    ).slice(0, 60);
    Vue.set(this.form, 'name', {
      valid: true,
      validators: { required, maxLength: maxLength(60) },
      value: name
    });
  }

  private setSaving(value: boolean) {
    this.saving = value;
  }

  public saveInfo() {
    this.setSaving(true);
    if(!this.edition) {
      this.saveNewFile(this.setDataToSave());
      return;
    }
    let data: Section = Object.assign({}, this.section);
    data.name = this.form.name.value;
    data.text = '';
    data.type = this.type;
    data.labelSections = this.tagSelected;
    data.component = this.componentSelected
    this.updateFile(data);
  }

  public removeFile() {
    let refs: any = this.$refs;
    refs.dropzoneRef.removeAllFiles();
    this.file = null;
    this.setFileName('');
  }

  public closeModal() {
    this.$EdModal.closeModal();
  }

  public onFileAdd(file: any) {
    setTimeout(() => {
      if(file.accepted) {
        this.ext = this.getExtension(file.name);
        if(!this.form.get('name').value) this.setFileName(file.name);
        this.file = file;
        this.newFileAdded = true;
      }
    }, 0);
  }

  /* Hooks */
  created() {
    if(this.edition) {
      this.initForm(this.section.name);
      this.ext = this.getExtension(this.section.assetsInfo[0].fileName);
      this.file = { name: this.section.name};
      this.tagSelected = this.section.labelSections;
      if(this.section.component) {
        this.componentSelected = this.components.find(c => c.id == this.section.component.id);
      } else if(this.components) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    } else {
      this.initForm();
      if(this.course.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
  }
}